import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Row,
  Col,
  Select,
  Tag,
} from 'antd';
import {
  GroupOutlined,
  FormOutlined,
  FieldTimeOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import * as Sentry from '@sentry/react';
import PropTypes from 'prop-types';

import OnTraccrButton from '../common/buttons/OnTraccrButton';

import AnalyticsChartTypePicker from './AnalyticsChartTypePicker';
import AnalyticsBreakDownTag from './AnalyticsBreakDownTag';
import AnalyticsGroups from './AnalyticsGroups';
import AnalyticsDatePicker from './AnalyticsDatePicker';
import AnalyticsDateFieldBreakdownSelector from './AnalyticsDateFieldBreakdownSelector';

import { getIdMap } from '../helpers/helpers';

import {
  getBoards,
  getBoardCardTemplates,
  getBoardDetails,
} from '../boards/state/boards.actions';
import { getTemplateDetails, getStatuses } from '../forms/state/forms.actions';
import { getCustomFields } from '../timecards/state/timecards.actions';
import { searchCostcodeOptions } from '../costcodes/state/costcodes.actions';
import {
  setAnalyticsConfig,
  updateAnalyticsFilters,
} from './state/analytics.actions';

import {
  BASE_FIELDS,
  FIELD_TYPES,
  AGGREGATE_OPTS,
  BREAKDOWN_OPTS,
  FORM_FIELD_BREAKDOWN_OPTS,
  BREAKDOWN_FILTER_SET,
  FIELD_BREAKDOWN_CHARTS,
  FIELDS_WITH_NO_BREAKDOWN,
  FIELD_IDS_WITH_NO_AGGREGATE,
  FORM_BASE_FIELDS,
  BREAKDOWN_DATE_OPT,
  FIELDS_CAN_BREAKDOWN,
  TIMECARD_BREAKDOWN_OPTS,
  TIMECARD_BASE_FIELDS,
  INACCESSIBLE_BOARD_LABEL,
} from './analytics.constants';
import {
  getValidCharts,
  getDateRangeFromConfig,
  canBreakdownByField,
  getFieldOptions,
  getFieldType,
  getTimecardCustomBreakdownSelectOptions,
  isTimecardCustomFieldBreakdownKey,
} from './analytics.helpers';
import { getFormOptions } from '../forms/formHelpers';
import useFilteredBoardList from '../common/hooks/useFilteredBoardList';
import OnTraccrCheckbox from '../common/inputs/OnTraccrCheckbox';

function AnalyticsHeader({
  onExport,
  analyticsConfig,
  type,
  currentBoardId,
}) {
  const dispatch = useDispatch();

  const boards = useSelector((state) => state.boards.boards);
  const formTemplates = useSelector((state) => state.forms.templates);
  const cardTemplates = useSelector((state) => state.boards.cardTemplates);
  const selectedDivisions = useSelector((state) => state.settings.selectedDivisions);
  const timecardCustomFields = useSelector((state) => state.timecards.customFields);
  const projects = useSelector((state) => state.projects.projects);
  const boardDetailsMap = useSelector((state) => state.boards.boardDetailsMap);
  const customersById = useSelector((state) => state.customers.customers);
  const vendorsById = useSelector((state) => state.vendors.vendors);
  const equipmentList = useSelector((state) => state.equipment.equipment);
  const labels = useSelector((state) => state.labels);
  const scheduleCategories = useSelector((state) => state.schedule.categories);
  const bucketsList = useSelector((state) => state.buckets.buckets);
  const users = useSelector((state) => state.users.users);
  const teams = useSelector((state) => state.teams.teams);
  const divisions = useSelector((state) => state.settings.divisions);

  const [costcodes, setCostcodes] = useState([]);

  const {
    category,
    boardId: selectedBoardId,
    formTemplateId,
    dateRange: selectedRange,
    datePreset,
    dateBreakdown: selectedDateBreakdown,
    fieldBreakdown,
    chartType: selectedChartType,
    fieldId,
    aggregate,
    breakdown,
    filterByFieldUpdatedTime = false,
    statusFilter = [],
  } = analyticsConfig;

  const isCardInStatus = type === 'newCardsInStatus';

  const projectIdMap = useMemo(() => getIdMap(projects), [projects]);
  const filteredBoardOptions = useFilteredBoardList({ asOptions: true });
  const boardOptions = useMemo(() => {
    if (type === 'boardCard') {
      return [{ value: currentBoardId, label: 'Card Data' }];
    }
    const hasSelectedBoard = selectedBoardId
      && !filteredBoardOptions.some((opt) => opt.value === selectedBoardId);
    if (hasSelectedBoard) {
      return [
        { value: selectedBoardId, label: INACCESSIBLE_BOARD_LABEL },
        ...filteredBoardOptions,
      ];
    }
    return filteredBoardOptions;
  }, [filteredBoardOptions, type, currentBoardId, selectedBoardId]);

  const formOptions = useMemo(() => getFormOptions({
    formTemplates,
    selectedDivisions,
    projectIdMap,
  }), [formTemplates, selectedDivisions, projectIdMap]);

  const {
    fields: sections = [],
  } = useMemo(() => {
    if (selectedBoardId) {
      const {
        [selectedBoardId]: { cardTypeId } = {},
      } = boards;
      const {
        [cardTypeId]: ourTemplate = {},
      } = cardTemplates;
      return ourTemplate;
    }
    if (formTemplateId) {
      const {
        [formTemplateId]: {
          formData: {
            sections: formSections = [],
          } = {},
        } = {},
      } = formTemplates;
      return { fields: formSections };
    }
    return {};
  }, [boards, selectedBoardId, formTemplateId, cardTemplates, formTemplates]);

  const dateRange = useMemo(() => (
    getDateRangeFromConfig({ dateRange: selectedRange, datePreset })
  ), [selectedRange, datePreset]);

  const {
    fieldOptions,
    fieldMap = {},
    fieldBreakdownOptions = [],
  } = useMemo(() => {
    let opts = [];

    if (selectedBoardId && type !== 'boardCard') {
      opts = [...BASE_FIELDS];
    }

    if (formTemplateId && type !== 'boardCard') {
      opts = [...FORM_BASE_FIELDS];
    }

    if (category === 'timecard') {
      opts = [...TIMECARD_BASE_FIELDS];
    }

    const fmap = {};
    const fieldBreakdowns = [];
    sections.forEach((section) => {
      const { fields = [], name: sectionName } = section;
      fields.forEach((field) => {
        const {
          id,
          selectedType,
        } = field;
        fmap[id] = field;

        if (FIELD_TYPES.has(selectedType)) {
          const fieldOptions = getFieldOptions({
            sectionName,
            field,
          });
          const addToFieldBreakdown = canBreakdownByField(field);
          fieldOptions.forEach((opt) => {
            if (addToFieldBreakdown && opt) {
              if (fieldId !== id) fieldBreakdowns.push(opt);
            }
            opts.push(opt);
          });
        }
      });
    });
    return { fieldOptions: opts, fieldMap: fmap, fieldBreakdownOptions: fieldBreakdowns };
  }, [selectedBoardId, sections, fieldId, type, formTemplateId, category]);

  const breakdownOpts = useMemo(() => {
    if (category === 'timecard') {
      const customOpts = getTimecardCustomBreakdownSelectOptions({
        customFieldsByDivision: timecardCustomFields,
        divisionIds: Array.from(selectedDivisions),
      });
      return [...TIMECARD_BREAKDOWN_OPTS, ...customOpts];
    }
    if (selectedBoardId) {
      switch (fieldId) {
        case 'conversion':
          return BREAKDOWN_OPTS.slice(0, 2);
        case 'newCardsInStatus':
          return BREAKDOWN_OPTS.filter(({ value }) => value === 'status');
        default:
          if (FIELDS_CAN_BREAKDOWN.has(getFieldType(fieldId, fieldMap))) {
            return [
              ...BREAKDOWN_OPTS,
              BREAKDOWN_DATE_OPT,
            ];
          }

          return BREAKDOWN_OPTS;
      }
    }

    if (formTemplateId && fieldId === 'numberOfSubmissions') {
      return BREAKDOWN_OPTS.slice(0, 1);
    }

    if (type === 'boardCard') return BREAKDOWN_OPTS;
    return FORM_FIELD_BREAKDOWN_OPTS;
  }, [
    selectedBoardId,
    fieldId,
    type,
    fieldMap,
    category,
    timecardCustomFields,
    selectedDivisions,
  ]);

  const timecardFilterEntityMaps = useMemo(() => ({
    projects,
    costcodes,
    users,
    teams,
    divisionsList: Object.values(divisions || {}),
    customers: Object.values(customersById || {}),
    vendors: Object.values(vendorsById || {}),
    equipment: equipmentList || [],
    labels: labels || [],
    formTemplates: Object.values(formTemplates || {}),
    scheduleCategories: scheduleCategories || [],
    buckets: bucketsList || [],
  }), [
    projects,
    costcodes,
    users,
    teams,
    divisions,
    customersById,
    vendorsById,
    equipmentList,
    labels,
    formTemplates,
    scheduleCategories,
    bucketsList,
  ]);

  const fieldType = getFieldType(fieldId, fieldMap);

  const onSelectConfig = useCallback((key) => (value) => {
    const update = { [key]: value?.target?.value ?? value };
    if (key === 'boardId' || key == 'formTemplateId' || key === 'category') {
      update.fieldId = null;
      update.groups = {};
      update.statusFilter = [];
      if (key === 'boardId') {
        update.formTemplateId = null;
        update.fieldBreakdown = null;
        update.category = null;
        if (breakdown) update.breakdown = breakdown?.filter((b) => !b.startsWith('field'))  || [];
      } else if (key === 'formTemplateId') {
        update.boardId = null;
        update.breakdown = [];
        update.fieldBreakdown = null;
        update.filterByFieldUpdatedTime = false;
        update.category = null;
      } else {
        update.boardId = null;
        update.formTemplateId = null;
        update.filterByFieldUpdatedTime = false;
        update.breakdown = [];
        update.fieldBreakdown = null;
      }
    } else if (key === 'fieldId') {
      const newFieldType = getFieldType(value, fieldMap);
      const newValidCharts = getValidCharts(newFieldType);
      if (!newValidCharts.has(selectedChartType)) {
        update.chartType = newValidCharts.size > 0 ? newValidCharts.values().next().value : null;
        if (fieldBreakdown && !FIELD_BREAKDOWN_CHARTS.has(update.chartType)) {
          update.fieldBreakdown = null;
          update.dateBreakdown = 'day';
        }
      }
      if (breakdown) {
        if (value === 'conversion') {
          update.breakdown = breakdown
            .filter((b) => b !== 'color' && !b.startsWith('field') && FIELDS_CAN_BREAKDOWN.has(newFieldType));
        } else if (!value.startsWith('field')) {
          update.breakdown = breakdown
            .filter((b) => !b.startsWith('field') && FIELDS_CAN_BREAKDOWN.has(newFieldType));
        } else {
          update.breakdown = breakdown?.filter(() => FIELDS_CAN_BREAKDOWN.has(newFieldType)) || [];
        }
      }

      update.filterByFieldUpdatedTime = false;
    } else if (key === 'chartType') {
      if (fieldBreakdown && !FIELD_BREAKDOWN_CHARTS.has(value)) {
        update.fieldBreakdown = null;
        update.dateBreakdown = 'none';
      }
      if (value === 'conversion') {
        update.breakdown = breakdown?.filter((b) => b !== 'color') || [];
      } else if (value === 'stackedLine' && !selectedDateBreakdown) {
        update.dateBreakdown = 'day';
      }
    } else if (key === 'breakdown') {
      const newBreakdown = new Set(value);
      const deletedBreakdown = breakdown?.filter((b) => !newBreakdown.has(b)) || [];
      if (deletedBreakdown.length > 0) {
        /*
          We need to clear out filters if the breakdown is removed.
          Otherwise when we add the breakdown back the filters will still be there.
        */
        const filterUpdate = deletedBreakdown.reduce((acc, key) => {
          acc[key] = [];
          return acc;
        }, {});
        dispatch(updateAnalyticsFilters({
          type,
          payload: filterUpdate,
        }));
      }

      if (newBreakdown.has(BREAKDOWN_DATE_OPT.value) && fieldBreakdown) {
        update.dateBreakdown = 'day';
        update.fieldBreakdown = null;
      }
    }
    dispatch(setAnalyticsConfig({
      type,
      payload: update,
    }));
  }, [
    dispatch,
    fieldMap,
    selectedChartType,
    selectedDateBreakdown,
    breakdown,
    fieldBreakdown,
    formTemplates,
    type,
  ]);

  const onSelectCategory = useCallback((value) => {
    let key;

    if (value === 'timecard') {
      key = 'category';
    } else if (value in boards || value === selectedBoardId) {
      key = 'boardId';
    } else if (value in formTemplates) {
      key = 'formTemplateId';
    } else {
      Sentry.withScope(() => {
        Sentry.captureException(new Error(`Invalid analytics category selected ${value}`));
      });
      return;
    }
    onSelectConfig(key)(value);
  }, [boards, formTemplates, onSelectConfig, selectedBoardId]);

  useEffect(() => {
    dispatch(getBoards());
    dispatch(getBoardCardTemplates());
    dispatch(getStatuses());
  }, [dispatch]);

  useEffect(() => {
    if (category !== 'timecard') return;
    dispatch(getCustomFields());
  }, [category]);

  useEffect(() => {
    let isActive = true;
    const loadCostcodeOptions = async () => {
      const options = await dispatch(searchCostcodeOptions());
      if (isActive) {
        setCostcodes(options.map(({ value, label }) => ({ id: value, name: label })));
      }
    };
    if (category === 'timecard') loadCostcodeOptions();
    return () => { isActive = false; };
  }, [category]);

  // Time Cards: drop breakdown tokens that are no longer in the Select (e.g. saved report or stale
  // state referencing a removed custom field or a field that became multi-select). Runs after
  // custom field definitions load so breakdownOpts includes eligible timecardCustomField:* options.
  useEffect(() => {
    if (category !== 'timecard' || !breakdown?.length) return;
    if (!Object.keys(timecardCustomFields || {}).length) return;
    const valid = new Set(breakdownOpts.map((o) => o.value));
    const pruned = breakdown.filter((b) => valid.has(b));
    if (pruned.length !== breakdown.length) {
      dispatch(setAnalyticsConfig({
        type,
        payload: { breakdown: pruned },
      }));
    }
  }, [
    category,
    breakdown,
    breakdownOpts,
    timecardCustomFields,
    type,
  ]);

  useEffect(() => {
    if (!formTemplateId) return;
    if (!(formTemplateId in formTemplates) || !formTemplates[formTemplateId].formData) {
      // Get form data from the backend
      dispatch(getTemplateDetails(formTemplateId));
    }
  }, [dispatch, formTemplates, formTemplateId]);

  useEffect(() => {
    // If date range changes, selected breakdown may be invalid
    // e.g. date range is one week, can't breakdown by quarter
    // Reset to day
    if (
      (
        (!selectedRange || selectedRange.length < 2)
        && !datePreset
      )
      || selectedDateBreakdown === 'day'
      || !selectedDateBreakdown
    ) return;
    const [startMoment, endMoment] = dateRange;
    const diff = moment.duration(endMoment.diff(startMoment));
    if (selectedDateBreakdown === 'year' && diff.asYears > 1) return;
    if (selectedDateBreakdown === 'quarter' && diff.asMonths() > 3) return;
    if (selectedDateBreakdown === 'month' && diff.asMonths() > 1) return;
    if (selectedDateBreakdown === 'week' && diff.asWeeks() > 1) return;
    dispatch(setAnalyticsConfig({
      type,
      payload: {
        dateBreakdown: 'day',
      },
    }));
  }, [dispatch, selectedRange, datePreset, selectedDateBreakdown, type]);

  useEffect(() => {
    if (isCardInStatus && breakdownOpts.length === 1 && onSelectConfig) {
      onSelectConfig('breakdown')([breakdownOpts[0].value]);
    }
  }, [fieldId, breakdownOpts, isCardInStatus]);

  useEffect(() => {
    if (selectedBoardId) dispatch(getBoardDetails(selectedBoardId, false));
  }, [selectedBoardId]);

  const targetId = selectedBoardId || formTemplateId || category;

  const isSelectedFieldDropdown = useMemo(() => {
    if (fieldId && fieldMap[fieldId]) {
      return fieldMap[fieldId].selectedType === 'dropdown';
    }

    return false;
  }, [fieldId, fieldMap]);

  const statuses = useMemo(() => (
    (boardDetailsMap?.[selectedBoardId]?.statuses || [])?.map((opt) => (
      { label: opt.title, value: opt.id }
    ))
  ), [boardDetailsMap, selectedBoardId]);

  const isCustomField = fieldId?.startsWith('field-');

  return (
    <div className="analytics-header">
      <Row justify="space-between">
        <Col>
          <Row align="middle" gutter={10}>
            <Col>
              <Select
                style={{ width: 250 }}
                showSearch
                optionFilterProp="label"
                onSelect={onSelectCategory}
                placeholder="Select Category"
                value={targetId}
                dropdownMatchSelectWidth={false}
              >
                { !currentBoardId && (
                  <Select.Option value="timecard">
                    <div>
                      <FieldTimeOutlined style={{ marginRight: 10 }} />
                      Time Cards
                    </div>
                  </Select.Option>
                )}
                {
                  boardOptions.map((opt) => (
                    <Select.Option {...opt} key={opt.value}>
                      <div>
                        <GroupOutlined style={{ marginRight: 10 }} />
                        {opt.label}
                      </div>
                    </Select.Option>
                  ))
                }
                {
                  formOptions.map((opt) => (
                    <Select.Option {...opt} key={opt.value}>
                      <div>
                        <FormOutlined style={{ marginRight: 10 }} />
                        {opt.label}
                      </div>
                    </Select.Option>
                  ))
                }
              </Select>
            </Col>
            {
              targetId
              && (
                <Col>
                  <AnalyticsDatePicker
                    type={type}
                    analyticsConfig={analyticsConfig}
                  />
                </Col>
              )
            }
            {
              (selectedRange || datePreset)
              && (
                <Col>
                  <AnalyticsDateFieldBreakdownSelector
                    fieldBreakdownOptions={fieldBreakdownOptions}
                    fieldMap={fieldMap}
                    analyticsConfig={analyticsConfig}
                    type={type}
                  />
                </Col>
              )
            }
            {
              (selectedRange || datePreset) && (selectedDateBreakdown || fieldBreakdown || FIELDS_WITH_NO_BREAKDOWN.has(fieldType))
              && (
              <Col>
                <AnalyticsChartTypePicker
                  selectedChartType={selectedChartType}
                  onSelect={onSelectConfig('chartType')}
                  fieldType={fieldType}
                />
              </Col>
              )
            }
          </Row>
        </Col>
        <Col>
          {targetId
            && fieldId
            && (selectedRange || datePreset)
            && selectedDateBreakdown
            && (
            <OnTraccrButton
              title="Export"
              onClick={onExport}
            />
            )}
        </Col>
      </Row>

      {targetId
        && (
        <Row align="top" gutter={10} style={{ marginTop: 10 }}>
          <Col>
            <Select
              options={fieldOptions}
              value={fieldId}
              style={{ minWidth: 250, maxWidth: 500 }}
              dropdownMatchSelectWidth={false}
              onSelect={onSelectConfig('fieldId')}
              placeholder="Select Field"
            />
          </Col>
          {
            fieldId
            && category !== 'timecard'
            && (
            <Col>
              <Select
                options={AGGREGATE_OPTS}
                style={{ width: 100 }}
                value={aggregate}
                onSelect={onSelectConfig('aggregate')}
                disabled={selectedChartType === 'list' || selectedChartType === 'pie' || FIELD_IDS_WITH_NO_AGGREGATE.has(fieldId)}
              />
            </Col>
            )
          }
          {
            (targetId) && fieldId
            && (
            <Col>
              <Select
                options={breakdownOpts}
                style={{ minWidth: 250 }}
                value={breakdown}
                onChange={onSelectConfig('breakdown')}
                mode="tags"
                placeholder="Breakdown by"
                disabled={isCardInStatus && breakdownOpts?.length === 1}
                tagRender={(props) => (
                  (BREAKDOWN_FILTER_SET.has(props.value)
                    || isTimecardCustomFieldBreakdownKey(props.value))
                    ? (
                      <AnalyticsBreakDownTag
                        key={targetId}
                        analyticsConfig={analyticsConfig}
                        type={type}
                        isClosable={!isCardInStatus || breakdownOpts?.length !== 1}
                        timecardFilterEntityMaps={timecardFilterEntityMaps}
                        {...props}
                      />
                    )
                    : <Tag {...props}>{props.label}</Tag>
                )}
              />
            </Col>
            )
          }
          {
            selectedBoardId && breakdown && breakdown.length > 0 && breakdown.includes('status')
            && (
            <Col>
              <AnalyticsGroups
                analyticsConfig={analyticsConfig}
                type={type}
              />
            </Col>
            )
          }
          {
            isSelectedFieldDropdown && (
              <OnTraccrCheckbox
                style={{ border: '1px solid #fff', background: 'white', padding: 3, borderRadius: 5 }}
                value={filterByFieldUpdatedTime}
                onChange={() => onSelectConfig('filterByFieldUpdatedTime')(!filterByFieldUpdatedTime)}
                label="Field Updates"
                hoverNode={(
                  <div>
                      If enabled, only show cards whose field was updated in the date range
                  </div>
                )}
                hoverIconProps={{ style: { marginLeft: 5, marginTop: 5 } }}
              />
            )
          }
          {
            selectedBoardId && isCustomField && (
              <Select
                options={statuses}
                style={{ minWidth: 250, marginLeft: isSelectedFieldDropdown ? 10 : 0 }}
                placeholder="Filter by entered status"
                mode="multiple"
                onChange={onSelectConfig('statusFilter')}
                value={statusFilter}
              />
            )
          }
        </Row>
        )}
    </div>
  );
}

/* eslint-disable react/forbid-prop-types */
AnalyticsHeader.propTypes = {
  onExport: PropTypes.func.isRequired,
  type: PropTypes.string,
  analyticsConfig: PropTypes.object,
  currentBoardId: PropTypes.string,
};

AnalyticsHeader.defaultProps = {
  type: null,
  analyticsConfig: {},
  currentBoardId: undefined,
};

export default AnalyticsHeader;
